import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, IconButton, Tabs, Tab, Divider, Chip, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import Apiconnection from '../utils/Apiconnection';

const PolicyModal = ({ open, onClose, initialTab = 'terms', onAccept }) => {
  const [activeTab, setActiveTab] = useState(initialTab === 'refund' ? 1 : 0);
  const [termsHtml, setTermsHtml] = useState('');
  const [refundHtml, setRefundHtml] = useState('');
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [loadingRefund, setLoadingRefund] = useState(false);

  useEffect(() => {
    setActiveTab(initialTab === 'refund' ? 1 : 0);
  }, [initialTab, open]);

  useEffect(() => {
    if (open) {
      if (!termsHtml) {
        const fetchTerms = async () => {
          setLoadingTerms(true);
          try {
            const response = await Apiconnection.post('content-page-get-by-type', {
              type: 'terms_and_conditions',
            });
            if (response?.data?.status && (response?.data?.data?.description || response?.data?.data?.policies)) {
              setTermsHtml(response.data.data.description || response.data.data.policies);
            } else {
              const fallbackRes = await Apiconnection.post('get-policies');
              if (fallbackRes?.data?.status && fallbackRes?.data?.data?.policies) {
                setTermsHtml(fallbackRes.data.data.policies);
              }
            }
          } catch (error) {
            console.error('Error fetching terms in PolicyModal:', error);
            try {
              const fallbackRes = await Apiconnection.post('get-policies');
              if (fallbackRes?.data?.status && fallbackRes?.data?.data?.policies) {
                setTermsHtml(fallbackRes.data.data.policies);
              }
            } catch (err) {
              console.error('Error fetching fallback policies in PolicyModal:', err);
            }
          } finally {
            setLoadingTerms(false);
          }
        };
        fetchTerms();
      }

      if (!refundHtml) {
        const fetchRefund = async () => {
          setLoadingRefund(true);
          try {
            const response = await Apiconnection.post('content-page-get-by-type', {
              type: 'refund_policy',
            });
            if (response?.data?.status && (response?.data?.data?.description || response?.data?.data?.policies)) {
              setRefundHtml(response.data.data.description || response.data.data.policies);
            }
          } catch (error) {
            console.error('Error fetching refund policy in PolicyModal:', error);
          } finally {
            setLoadingRefund(false);
          }
        };
        fetchRefund();
      }
    }
  }, [open, termsHtml, refundHtml]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="policy-modal-title"
      aria-describedby="policy-modal-content"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          width: '92%',
          maxWidth: '680px',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.25)',
          outline: 'none',
          overflow: 'hidden',
          fontFamily: "'Inter', 'Poppins', sans-serif",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            pt: 3,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f1f5f9',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                backgroundColor: '#f0f7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#004777',
              }}
            >
              {activeTab === 0 ? <GavelOutlinedIcon /> : <MonetizationOnOutlinedIcon />}
            </Box>
            <Box>
              <Typography
                id="policy-modal-title"
                variant="h6"
                sx={{ fontWeight: 800, color: '#0f172a', fontSize: '19px' }}
              >
                {activeTab === 0 ? 'Terms & Conditions' : 'Refund & Cancellation Policy'}
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '13px', fontWeight: 500 }}>
                Please review our customer agreement and booking policies
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{
              color: '#64748b',
              backgroundColor: '#f8fafc',
              '&:hover': { backgroundColor: '#e2e8f0', color: '#0f172a' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 1, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '14px',
                fontFamily: "'Inter', sans-serif",
                color: '#64748b',
                minHeight: '44px',
                py: 1,
                '&.Mui-selected': {
                  color: '#004777',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#004777',
                height: '3px',
                borderRadius: '3px',
              },
            }}
          >
            <Tab label="Terms & Conditions" />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Refund Policy</span>
                  <Chip
                    label="Non-Refundable"
                    size="small"
                    sx={{
                      backgroundColor: '#fee2e2',
                      color: '#b91c1c',
                      fontSize: '10px',
                      fontWeight: 800,
                      height: '18px',
                    }}
                  />
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Modal Body */}
        <Box
          id="policy-modal-content"
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: '#f8fafc' },
            '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '4px' },
          }}
        >
          {activeTab === 0 ? (
            <Box>
              {loadingTerms && !termsHtml ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={36} sx={{ color: '#004777' }} />
                </Box>
              ) : termsHtml ? (
                <Box
                  sx={{
                    color: '#334155',
                    fontSize: '14px',
                    lineHeight: 1.7,
                    '& ul': { paddingLeft: '22px', mb: 2 },
                    '& li': { mb: 1.2 },
                    '& strong': { color: '#0f172a' },
                    '& p': { mb: 1.2 },
                    '& h1, & h2, & h3, & h4': { color: '#0f172a', fontWeight: 700, mb: 1.5 },
                  }}
                  dangerouslySetInnerHTML={{ __html: termsHtml }}
                />
              ) : (
                <Box sx={{ color: '#334155', fontSize: '14px', lineHeight: 1.7 }}>
                  <Typography sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, fontSize: '15px' }}>
                    Prime Park Reservation Terms:
                  </Typography>
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>Timing & Duration:</strong> Your reservation is valid strictly for the specified check-in and check-out dates and times. Overstay hours will be billed on-site at posted standard facility rates ($10/hr or $15–$18/day).
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>Oversized Vehicle Fee:</strong> Small SUVs, midsize/large SUVs, trucks, and vans will be subject to an oversized vehicle surcharge ($5/day, $7/day, or $10/day) payable directly upon arrival.
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>Drop-off Vehicle Inspection:</strong> Customers are required to photograph all sides of their vehicle upon arrival at the facility. No damage claim can be processed without validated drop-off photographs.
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>Key Handover:</strong> You must leave your vehicle keys with the on-duty parking attendant. Failure to leave keys may result in towing fees ranging from $75 to $150.
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>In / Out Privileges:</strong> In/out privileges are strictly prohibited. Each reservation permit grants a single entry and exit only.
                    </li>
                    <li style={{ marginBottom: '10px' }}>
                      <strong>On-Site Balances:</strong> All remaining charges and on-site fees must be settled in full prior to vehicle retrieval.
                    </li>
                  </ul>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              {loadingRefund && !refundHtml ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={36} sx={{ color: '#004777' }} />
                </Box>
              ) : refundHtml ? (
                <Box
                  sx={{
                    color: '#334155',
                    fontSize: '14px',
                    lineHeight: 1.7,
                    '& ul': { paddingLeft: '22px', mb: 2 },
                    '& li': { mb: 1.2 },
                    '& strong': { color: '#0f172a' },
                    '& p': { mb: 1.2 },
                    '& h1, & h2, & h3, & h4': { color: '#0f172a', fontWeight: 700, mb: 1.5 },
                  }}
                  dangerouslySetInnerHTML={{ __html: refundHtml }}
                />
              ) : (
                <Box>
                  <Box
                    sx={{
                      backgroundColor: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderRadius: '12px',
                      p: 2,
                      mb: 3,
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, color: '#92400e', fontSize: '14px', mb: 0.5 }}>
                      Notice: All Online Reservations Are Non-Refundable
                    </Typography>
                    <Typography sx={{ color: '#78350f', fontSize: '13px', lineHeight: 1.5 }}>
                      Please read the refund and cancellation conditions below carefully before completing your booking.
                    </Typography>
                  </Box>

                  <Box sx={{ color: '#334155', fontSize: '14px', lineHeight: 1.7 }}>
                    <Typography sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, fontSize: '15px' }}>
                      Refund &amp; Cancellation Policy Details:
                    </Typography>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      <li style={{ marginBottom: '12px' }}>
                        <strong>Mandatory Non-Refundable Reservation Fee:</strong> All reservation deposits and online booking amounts collected at checkout are strictly 100% non-refundable once the booking transaction is processed.
                      </li>
                      <li style={{ marginBottom: '12px' }}>
                        <strong>Cancellations:</strong> In the event of a customer-initiated cancellation, the reservation fee is strictly forfeited. For canceled online vouchers/reservations, customers are required to pay for one day of parking plus a $10 service fee.
                      </li>
                      <li style={{ marginBottom: '12px' }}>
                        <strong>No Partial Refunds or Credits:</strong> No refunds, price reductions, or account credits will be provided for late check-in, early retrieval, or unused days/hours.
                      </li>
                      <li style={{ marginBottom: '12px' }}>
                        <strong>No-Shows:</strong> If a vehicle is not dropped off during the reserved period without prior notice, the entire reservation is forfeited without refund eligibility.
                      </li>
                      <li style={{ marginBottom: '12px' }}>
                        <strong>Extensions & Adjustments:</strong> Reservation extensions cannot be processed online as refund claims or offsets; all extra time must be paid on-site at standard facility rates.
                      </li>
                    </ul>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            py: 2.5,
            borderTop: '1px solid #f1f5f9',
            backgroundColor: '#f8fafc',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderRadius: '100px',
              px: 3,
              py: 1,
              fontWeight: 700,
              textTransform: 'none',
              borderColor: '#cbd5e1',
              color: '#475569',
              '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f1f5f9' },
            }}
          >
            Close
          </Button>
          {onAccept && (
            <Button
              variant="contained"
              onClick={() => {
                onAccept();
                onClose();
              }}
              sx={{
                borderRadius: '100px',
                px: 4,
                py: 1,
                fontWeight: 700,
                textTransform: 'none',
                backgroundColor: '#004777',
                color: '#fff',
                boxShadow: '0 4px 14px rgba(0, 71, 119, 0.25)',
                '&:hover': { backgroundColor: '#003355' },
              }}
            >
              I Understand &amp; Accept
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default PolicyModal;
